import { useState, useEffect } from 'react';
import { useCalendarStore } from '@/store/calendarStore';
import { FiFilter, FiX, FiCheck } from 'react-icons/fi';

/**
 * Panel de filtros para el calendario
 */
export default function FilterPanel() {
  const { 
    filters, 
    toggleShowCitas, 
    toggleShowLecturas, 
    toggleShowEventos,
    toggleShowFestividadesJudias,
    toggleTipoLectura,
    clearFilters,
    lecturas 
  } = useCalendarStore();
  
  const [showPanel, setShowPanel] = useState(false);
  const [tiposLecturaDisponibles, setTiposLecturaDisponibles] = useState([]);

  // Extraer tipos únicos de lecturas
  useEffect(() => {
    const tipos = [...new Set(lecturas.map(l => l.tipo))].filter(Boolean).sort();
    setTiposLecturaDisponibles(tipos);
  }, [lecturas]);

  const hasActiveFilters = !filters.showCitas || !filters.showLecturas || !filters.showEventos || 
    filters.showFestividadesJudias || filters.tiposLectura.length > 0;

  return (
    <div className="relative">
      {/* Botón para abrir panel */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg shadow-md transition-all
          ${hasActiveFilters 
            ? 'bg-primary text-white hover:bg-blue-600' 
            : 'bg-white text-gray-700 hover:bg-gray-50'
          }
        `}
        title="Filtros"
      >
        <FiFilter className="w-5 h-5" />
        <span className="hidden sm:inline">Filtros</span>
        {hasActiveFilters && (
          <span className="bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
            !
          </span>
        )}
      </button>

      {/* Panel de filtros */}
      {showPanel && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setShowPanel(false)}
          />
          
          {/* Panel */}
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl z-50 p-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Filtros</h3>
              <button
                onClick={() => setShowPanel(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Filtros principales */}
            <div className="space-y-3 mb-4">
              <h4 className="font-semibold text-sm text-gray-600 uppercase tracking-wide">
                Mostrar
              </h4>
              
              <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.showCitas}
                  onChange={toggleShowCitas}
                  className="w-5 h-5 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-[#4A90E2]"></span>
                  <span className="font-medium text-gray-700">Citas Ordenadas</span>
                </div>
                {filters.showCitas && <FiCheck className="text-green-500" />}
              </label>

              <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.showLecturas}
                  onChange={toggleShowLecturas}
                  className="w-5 h-5 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-[#E24A90]"></span>
                  <span className="font-medium text-gray-700">Lecturas</span>
                </div>
                {filters.showLecturas && <FiCheck className="text-green-500" />}
              </label>

              <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.showEventos}
                  onChange={toggleShowEventos}
                  className="w-5 h-5 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-[#90E24A]"></span>
                  <span className="font-medium text-gray-700">Eventos</span>
                </div>
                {filters.showEventos && <FiCheck className="text-green-500" />}
              </label>

              <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.showFestividadesJudias}
                  onChange={toggleShowFestividadesJudias}
                  className="w-5 h-5 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center gap-2 flex-1">
                  <span className="w-3 h-3 rounded-full bg-[#9B59B6]"></span>
                  <span className="font-medium text-gray-700">Festividades Judías</span>
                </div>
                {filters.showFestividadesJudias && <FiCheck className="text-green-500" />}
              </label>
            </div>

            {/* Filtro de tipos de lecturas */}
            {filters.showLecturas && tiposLecturaDisponibles.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-sm text-gray-600 uppercase tracking-wide mb-3">
                  Tipos de Lectura
                </h4>
                <div className="space-y-2">
                  {tiposLecturaDisponibles.map(tipo => {
                    const isSelected = filters.tiposLectura.length === 0 || filters.tiposLectura.includes(tipo);
                    const lecturaCount = lecturas.filter(l => l.tipo === tipo).length;
                    
                    // Colores por tipo
                    const colorMap = {
                      'Torah': '#90E24A',
                      'Profetas': '#4AC4E2',
                      'Brit Hadashá': '#E2C44A',
                      'Lectura Diaria': '#50C878',
                      'Salmos': '#9B59B6',
                    };
                    const color = colorMap[tipo] || '#999999';
                    
                    return (
                      <label 
                        key={tipo}
                        className={`
                          flex items-center gap-3 cursor-pointer p-2 rounded transition-all
                          ${isSelected ? 'bg-gray-50' : 'opacity-50'}
                          hover:bg-gray-100
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTipoLectura(tipo)}
                          className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: color }}
                          ></span>
                          <span className="text-sm font-medium text-gray-700">{tipo}</span>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                          {lecturaCount}
                        </span>
                      </label>
                    );
                  })}
                </div>
                
                {filters.tiposLectura.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Mostrando {filters.tiposLectura.length} de {tiposLecturaDisponibles.length} tipos
                  </p>
                )}
              </div>
            )}

            {/* Botón para limpiar filtros */}
            {hasActiveFilters && (
              <div className="border-t pt-4 mt-4">
                <button
                  onClick={() => {
                    clearFilters();
                    setShowPanel(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                >
                  Limpiar todos los filtros
                </button>
              </div>
            )}

            {/* Resumen */}
            <div className="border-t pt-4 mt-4 text-xs text-gray-500">
              <p>
                {filters.showCitas && 'Citas '}
                {filters.showLecturas && 'Lecturas '}
                {filters.showEventos && 'Eventos '}
                {filters.showFestividadesJudias && 'Festividades '}
                activos
              </p>
              {filters.tiposLectura.length > 0 && (
                <p className="mt-1">
                  Filtrado por: {filters.tiposLectura.join(', ')}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

